import React from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  LinearProgress,
  Chip,
  Fade
} from '@mui/material';
import { dashboardTheme } from '../styles/dashboardTheme';
import WidgetInfoTooltip from './WidgetInfoTooltip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import TargetIcon from '@mui/icons-material/TrackChanges';
import AddIcon from '@mui/icons-material/Add';

/**
 * GoalsSummary Component
 * Shows active goals with progress
 */
const GoalsSummary = ({ goals = [], onManageGoals }) => {
  const activeGoals = goals.filter(goal => goal.status === 'active').slice(0, 3);
  const completedCount = goals.filter(goal => goal.status === 'completed').length;
  const totalGoals = goals.length;

  const calculateProgress = (goal) => {
    if (goal.progress) return goal.progress;
    if (goal.completedSteps && goal.totalSteps) {
      return (goal.completedSteps / goal.totalSteps) * 100;
    }
    return 0;
  };

  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 2, 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: '#ffffff',
        borderRadius: '16px',
        border: '1px solid rgba(0,0,0,0.05)',
        boxShadow: dashboardTheme.shadows.medium,
        transition: dashboardTheme.animations.transition,
        '&:hover': {
          boxShadow: dashboardTheme.shadows.large
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            Active Goals
          </Typography>
          <WidgetInfoTooltip id="goals" />
        </Box>
        {totalGoals > 0 && (
          <Chip 
            label={`${completedCount}/${totalGoals}`}
            size="small"
            color={completedCount === totalGoals ? 'success' : 'default'}
          />
        )}
      </Box>

      {activeGoals.length > 0 ? (
        <List sx={{ flex: 1, overflow: 'auto' }}>
          {activeGoals.map((goal, index) => {
            const progress = calculateProgress(goal);
            return (
              <ListItem 
                key={goal._id || index}
                sx={{ 
                  px: 0,
                  py: 2,
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  borderBottom: index < activeGoals.length - 1 ? 1 : 0,
                  borderColor: 'divider'
                }}
              >
                {/* The text takes the whole row beside the icon; the percentage sits at
                    the end of the progress bar. It used to share the text's row, which on a
                    phone left long goals squeezed into a narrow column (ST-20261007-0001). */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                  <ListItemIcon sx={{ minWidth: 32, mt: 0.25 }}>
                    <TargetIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    sx={{ m: 0, minWidth: 0 }}
                    primary={
                      <Typography variant="body2" fontWeight="medium" sx={{ overflowWrap: 'anywhere' }}>
                        {goal.title || goal.description}
                      </Typography>
                    }
                    secondary={goal.targetDate && (
                      <Typography variant="caption" color="text.secondary">
                        Target: {new Date(goal.targetDate).toLocaleDateString()}
                      </Typography>
                    )}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 4 }}>
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    sx={{ 
                      flex: 1,
                      height: 8, 
                      borderRadius: 4,
                      backgroundColor: 'rgba(0,0,0,0.05)',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        background: dashboardTheme.colors.success.gradient
                      }
                    }}
                  />
                  <Typography variant="body2" fontWeight="bold" color="success.main" sx={{ minWidth: 36, textAlign: 'right' }}>
                    {Math.round(progress)}%
                  </Typography>
                </Box>
                {goal.notes && (
                  <Typography variant="caption" color="text.secondary" sx={{ pl: 4, mt: 1 }}>
                    {goal.notes}
                  </Typography>
                )}
              </ListItem>
            );
          })}
        </List>
      ) : (
        <Box 
          sx={{ 
            flex: 1, 
            display: 'flex', 
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'text.secondary'
          }}
        >
          <TargetIcon sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
          <Typography variant="body2" align="center" gutterBottom>
            No active goals yet
          </Typography>
          <Typography variant="caption" align="center">
            Set your first goal to track progress
          </Typography>
        </Box>
      )}

      <Button
        variant={activeGoals.length > 0 ? "outlined" : "contained"}
        fullWidth
        startIcon={activeGoals.length > 0 ? null : <AddIcon />}
        onClick={onManageGoals}
        sx={{ mt: 2 }}
      >
        {activeGoals.length > 0 ? 'Manage Goals' : 'Set Your First Goal'}
      </Button>
    </Paper>
  );
};

export default GoalsSummary;